import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  CurrencyDollarIcon,
  ClipboardDocumentCheckIcon,
  BellAlertIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  DocumentArrowUpIcon,
  TruckIcon,
  EnvelopeIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAppStore } from '@/stores/appStore';
import { useAuthStore } from '@/stores/authStore';
import { brandsApi, alertsApi } from '@/services/api';
import NotificationCenter from './NotificationCenter';
import DocumentAnalyzer from './DocumentAnalyzer';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Purchase Orders', href: '/pos', icon: ClipboardDocumentListIcon },
  { name: 'Emails', href: '/emails', icon: EnvelopeIcon },
  { name: 'Permits', href: '/permits', icon: DocumentCheckIcon },
  { name: 'Payments', href: '/payments', icon: CurrencyDollarIcon },
  { name: 'Shipments', href: '/shipments', icon: TruckIcon },
  { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentCheckIcon },
  { name: 'Alerts', href: '/alerts', icon: BellAlertIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen, setBrands, isMobile, setIsMobile } = useAppStore();
  const { user, logout } = useAuthStore();
  const [showDocAnalyzer, setShowDocAnalyzer] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch brands on mount
  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const response = await brandsApi.list();
      return response.data.data;
    },
  });

  // Fetch active alerts count (data available in cache for other components)
  useQuery({
    queryKey: ['alerts-count'],
    queryFn: async () => {
      const response = await alertsApi.getStats();
      return response.data.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  useEffect(() => {
    if (brandsData) {
      setBrands(brandsData);
    }
  }, [brandsData, setBrands]);

  // Check mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setIsMobile, setSidebarOpen]);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, setSidebarOpen]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img
              src="/enana-logo.svg"
              alt="Enana"
              className="w-8 h-8 object-contain"
            />
            <span className="text-lg font-semibold text-gray-900">Enana PO</span>
          </div>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </NavLink>
          ))}
        </nav>

        {/* Brand Pills */}
        {brandsData && brandsData.length > 0 && (
          <div className="px-4 py-4 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase mb-3">Brands</p>
            <div className="flex flex-wrap gap-2">
              {brandsData.map((brand: { id: string; code: string; name: string }) => (
                <span
                  key={brand.id}
                  className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                  title={brand.name}
                >
                  {brand.code}
                </span>
              ))}
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className={`min-h-screen transition-all duration-200 ${!isMobile && sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-2">
              {/* Upload Document Button */}
              <button
                onClick={() => setShowDocAnalyzer(true)}
                className="p-2 text-gray-500 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                title="Upload & Analyze Document"
              >
                <DocumentArrowUpIcon className="w-5 h-5" />
              </button>

              {/* Notification Center */}
              <NotificationCenter />

              {/* User info and logout */}
              <div className="flex items-center space-x-2 ml-2 pl-2 border-l border-gray-200">
                {user && (
                  <span className="text-sm text-gray-600 hidden sm:inline">
                    {user.full_name || user.email}
                  </span>
                )}
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Document Analyzer Modal */}
      <DocumentAnalyzer
        isOpen={showDocAnalyzer}
        onClose={() => setShowDocAnalyzer(false)}
      />
    </div>
  );
}
